import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useFetchMeasureTypesQuery, useFetchRoadDiagnosticsSegmentsQuery, useFetchRoadMeasuresQuery, useLazyFetchRoadMeasureResultsQuery } from "../../../../api/rtk/base/slices/diagnosticsApiSlice";
import { prepareTableData } from "../../../../features/dataFormatFunctions/dataFormatter";
import { getDataArrayWithMaintenanceEnding, getDataArrayWithMaintenancePosition } from "../../../../features/dataFormatFunctions/formatMaintenancePoint";
import { getItemWebStorage } from "../../../../features/webStorageSaver";
import { useAppSelector } from "../../../../stateManagement/hooks/redux";
import { setCurrentRoadDiagnosticsSegmentNew, setTableCaption, setTableDataCount, showToastMessage } from "../../../../stateManagement/reducers";
import { setCurrentRoadMeasure } from "../../../../stateManagement/reducers/diagnostics/RoadMeasuresSlice";
import ContentHeader from "../../../../widgets/contentHeader/ContentHeader";
import LoaderWrapper from "../../../../widgets/loaderWrapper/LoaderWrapper";
import TableInfoWidget from "../../../../widgets/table/tableAddons/tableInfoWidget/TableInfoWidget";
import { prepareQueryArgsForTableFetch } from "../../../../widgets/table/tableFeatures/tableFetchFunctions";
import { configureTableLazyState, defineTableColumnsFromArray, defineTableFilters, TableEvents, tableStateModifyHandle } from "../../../../widgets/table/tableFeatures/tableFunctions";
import { LineValuesHeaderColumnGroupTemplate, SideLineValuesHeaderColumnGroupTemplate } from "../../../../widgets/table/tableFeatures/tableTemplates/tableHeaderColumnGroupTemplates/roadMeasureResults";
import { getBasicTablePaginator } from "../../../../widgets/table/tableFeatures/tableTemplates/tablePaginatorTemplates";
import TableWidget from "../../../../widgets/table/tableWidget/TableWidget";
import ToastMessageContainer from "../../../../widgets/toastMessageContainer/ToastMessageContainer";
import { defineRoadMeasureResultsTableLineColumnsStructure, defineRoadMeasureResultsTableSideColumnsStructure, defineTableLineColumnsPattern, defineTableSideColumnsPattern } from "./defineTableLineColumns";

const roadMeasureResultsCommonColumnsStructure: any = [
    {
        headerName: "Начало, км",
        field: "Position",
        align: "right",
        valueType: "float",
        textWrap: false
    },
    {
        headerName: "Км+",
        field: "MaintenancePosition",
        valueType: "int",
        canBeFiltered: false,
        align: "right",
        textWrap: false
    }
]

const measureTypesWithSides = ['RoadMeasureGaugeDepth']

export default function TableNestedRoadMeasureResults() {
    const dispatch = useDispatch()
    const { eventId, measureId, measureTypeId } = useParams();
    const [tableTitle, setTableTitle] = useState<string>("")
    const [tableValueUrl, setTableValueUrl] = useState<string>("")
    const [hasLineValues, setHasLineValues] = useState<boolean>(false)
    const [hasSideValues, setHasSideValues] = useState<boolean>(false)
    const [forwardLineCount, setForwardLineCount] = useState<number | null>(null)
    const [reverseLineCount, setReverseLineCount] = useState<number | null>(null)
    const [measureTypeClassName, setMeasureTypeClassName] = useState<string | null>(null)
    const [columns, setColumns] = useState<any>(null)
    const [headerColumnGroup, setHeaderColumnGroup] = useState<any>(null)
    const [tableState, setTableState] = useState<any>(null)
    const [paginator, setPaginator] = useState<any>({})
    const [paginatorPageInputTooltip, setPaginatorPageInputTooltip] = useState<any>('Введите номер страницы.')
    const [tableValue, setTableValue] = useState<any>([])
    const [tableLoading, setTableLoading] = useState<boolean>(true)

    const tableInfoWindowIsShown = useAppSelector(state => state.contentHeader.tableInfoWindowIsShown)
    const currentRoadMeasure = useAppSelector(state => state.roadMeasures.currentRoadMeasure)
    const currentRoadDiagnosticsSegment = useAppSelector(state => state.roadDiagnosticsSegments.currentRoadDiagnosticsSegment)
    const dataCount = useAppSelector(state => state.table.dataCount)

    const { data: measureTypes, isFetching: isFetchingMeasureTypes } = useFetchMeasureTypesQuery({})
    const [fetchRoadMeasureResults, { isError: isErrorTableValue }] = useLazyFetchRoadMeasureResultsQuery()

    const { data: requestedDiagnosticsSegment,
        isFetching: requestedDiagnosticsSegmentIsFetching,
        isError: requestedDiagnosticsSegmentIsError
    } = useFetchRoadDiagnosticsSegmentsQuery({ top: 1, filterString: `EventId eq ${eventId}` }, { skip: currentRoadDiagnosticsSegment })

    useEffect(() => {
        if (requestedDiagnosticsSegment?.value[0]) {
            dispatch(setCurrentRoadDiagnosticsSegmentNew(requestedDiagnosticsSegment?.value[0]))
        }
    }, [requestedDiagnosticsSegment])

    const { data: requestedRoadMeasure,
        isFetching: requestedRoadMeasureIsFetching,
        isError: requestedRoadMeasureIsError
    } = useFetchRoadMeasuresQuery({ top: 1, filterString: `MeasureId eq ${measureId}` }, { skip: currentRoadMeasure })

    useEffect(() => {
        if (requestedRoadMeasure?.value[0]) {
            dispatch(setCurrentRoadMeasure(requestedRoadMeasure?.value[0]))
        }
    }, [requestedRoadMeasure])

    useEffect(() => {
        if (currentRoadMeasure) {
            const { ForwardLineCount, ReverseLineCount } = currentRoadMeasure ?? {}
            setForwardLineCount(ForwardLineCount)
            setReverseLineCount(ReverseLineCount)
        }
    }, [currentRoadMeasure])

    useEffect(() => {
        dispatch(setTableCaption("Результаты измерений"))
    },[])

    useEffect(() => {
        if (measureTypes) {
            const currentMeasureType = measureTypes.find((measureType: any) => measureType?.MeasureTypeId == measureTypeId)
            const { Caption, ClassName, MeasureUnit, HasLineValues, url } = currentMeasureType ?? {}
            if (Caption) {
                const tableTitle = `Таблица «Результаты измерений» - ${Caption}` + (MeasureUnit ? ` (${MeasureUnit})` : '')
                setTableTitle(tableTitle)
            }
            if (ClassName) {
                setMeasureTypeClassName(ClassName)
                setHasSideValues(measureTypesWithSides.includes(ClassName))
                setTableValueUrl(url)
            }
            setHasLineValues(HasLineValues)
        }
    }, [measureTypeId, measureTypes])

    useEffect(() => {
        const paginatorTemplate = getBasicTablePaginator(paginatorPageInputTooltip, setPaginatorPageInputTooltip, tableState, setTableState, dataCount, measureTypeClassName)
        setPaginator(paginatorTemplate)
    }, [dataCount, tableState, paginatorPageInputTooltip, measureTypeClassName])

    useEffect(() => {
        if (measureTypeClassName) {
            let columnsStructure = [...roadMeasureResultsCommonColumnsStructure]
            if (hasLineValues && reverseLineCount != null && forwardLineCount != null) {
                const columnsLines = defineTableLineColumnsPattern(reverseLineCount, forwardLineCount)
                let headerColumnGroup = null
                let addColumnsStructure = []
                if (hasSideValues) {
                    const sides = defineTableSideColumnsPattern(columnsLines)
                    headerColumnGroup = SideLineValuesHeaderColumnGroupTemplate({ ...columnsLines, sides })
                    addColumnsStructure = defineRoadMeasureResultsTableSideColumnsStructure(sides)
                } else {
                    headerColumnGroup = LineValuesHeaderColumnGroupTemplate(columnsLines)
                    addColumnsStructure = defineRoadMeasureResultsTableLineColumnsStructure(columnsLines)
                }
                setHeaderColumnGroup(headerColumnGroup)
                columnsStructure = [...columnsStructure, ...addColumnsStructure]
            }
            let columns = defineTableColumnsFromArray(columnsStructure)
            let filters = defineTableFilters(columns)
            setColumns(columns)
            setTableState(getItemWebStorage("tablesState")?.[measureTypeClassName] ?? configureTableLazyState({ filters }))
        }
    }, [measureTypeClassName, hasLineValues, hasSideValues, reverseLineCount, forwardLineCount])

    async function getTableValue(tableValueUrl: string) {
        setTableLoading(true)
        let { sortString, filterStringArray, page }: any = prepareQueryArgsForTableFetch(tableState, columns)
        filterStringArray.push(`MeasureId eq ${measureId}`)
        const { data: roadMeasureResults } = await fetchRoadMeasureResults({ url: tableValueUrl, pages: true, top: 100, order: sortString, filterStringArray, skip: page * 100 })
        let value = roadMeasureResults?.value
        value = prepareTableData(value, columns)
        value = getDataArrayWithMaintenancePosition(value)
        value = getDataArrayWithMaintenanceEnding(value)
        setTableValue(value)
        let dataCount = roadMeasureResults?.['@xdata.count']
        dispatch(setTableDataCount(dataCount))
        setTableLoading(false)
    }

    useEffect(() => {
        if (columns && tableValueUrl) {
            getTableValue(tableValueUrl)
        }
    }, [tableValueUrl, columns, tableState])

    const onSort = (state: any) => tableStateModifyHandle(state, TableEvents.sort, setTableState, measureTypeClassName)
    const onPage = (state: any) => tableStateModifyHandle(state, TableEvents.page, setTableState, measureTypeClassName)
    const onFilter = (state: any) => tableStateModifyHandle(state, TableEvents.filter, setTableState, measureTypeClassName)

    useEffect(() => {
        if (isErrorTableValue) {
            setTableLoading(false)
        }
    }, [isErrorTableValue])

    useEffect(() => {
        if (isErrorTableValue) {
            dispatch(showToastMessage({ messageText: "Ошибка загрузки таблицы", messageType: "error" }))
        }
    }, [isErrorTableValue])

    return (
        <>
            <ContentHeader title={tableTitle} type="table" option="roadMeasureResults" />
            <div className="table-widget-with-toast with-content-header">
                <ToastMessageContainer />
                <LoaderWrapper isLoading={tableLoading} >
                    {tableInfoWindowIsShown && <TableInfoWidget type="roadMeasureResults" />}
                    <TableWidget
                        tableValue={tableValue}
                        tableState={tableState}
                        headerColumnGroup={headerColumnGroup}
                        columns={columns}
                        dataCount={dataCount}
                        paginatorTemplate={paginator}
                        pageSize={100}
                        onPage={onPage}
                        onSort={onSort}
                        onFilter={onFilter}
                        isLazy={true}
                    />
                </LoaderWrapper>
            </div >
        </>
    )
}

